<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orbit-modules">
    <div v-for="m in modules" :key="m.key" class="orbit-module">
      <span class="orbit-module-icon" aria-hidden="true">
        <!-- eslint-disable-next-line vue/no-v-html -->
        <svg viewBox="0 0 24 24" v-html="m.icon"></svg>
      </span>
      <h2 class="orbit-module-title">{{ m.title }}</h2>
      <p class="orbit-module-text">{{ m.text }}</p>
      <button
        type="button"
        class="orbit-module-more"
        :aria-expanded="open === m.key"
        @click="open = open === m.key ? null : m.key"
      >
        Module overview {{ open === m.key ? '↑' : '→' }}
      </button>
      <ul v-if="open === m.key" class="orbit-module-list">
        <li v-for="item in m.contents" :key="item">{{ item }}</li>
      </ul>
      <button
        type="button"
        class="orbit-module-go"
        @click="$emit('pick', m.key)"
      >
        {{ m.key === current ? `${actionLabel} (current)` : actionLabel }}
      </button>
    </div>
  </div>
</template>

<script>
// Orbit HRM's three modules as cards (the sign-in page and the in-app picker). The keys match
// OrbitSections (PHP): hr, recruitment, admin.
const ICON = {
  users:
    '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
  userPlus:
    '<path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/>',
  briefcase:
    '<rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>',
};

export const ORBIT_MODULES = [
  {
    key: 'hr',
    icon: ICON.users,
    title: 'Human Resources',
    text: 'Employee records, personnel administration, leave, time tracking, performance and expenses.',
    contents: [
      'Employees and My Info',
      'Directory',
      'Leave',
      'Time & Attendance',
      'Performance',
      'Claims (expenses)',
      'Buzz',
    ],
  },
  {
    key: 'recruitment',
    icon: ICON.userPlus,
    title: 'Recruitment',
    text: 'Job openings, candidate pipeline, interviews and offers, from sourcing to hire, with ANNA.',
    contents: [
      'Vacancies',
      'Candidates, through to offer and hire',
      'Interview calendar and recordings',
      'Job offers',
      'ANNA, the AI recruiter, and her settings',
    ],
  },
  {
    key: 'admin',
    icon: ICON.briefcase,
    title: 'Administration',
    text: 'Users, job titles, organization, qualifications and system configuration.',
    contents: [
      'Users and roles',
      'Job titles, pay grades, statuses and work shifts',
      'Organization: information, locations, structure',
      'Qualifications and nationalities',
      'Branding, email, localization, modules, sign-in methods',
      'Maintenance: purge and access records',
    ],
  },
];

export default {
  name: 'OrbitModuleCards',
  props: {
    actionLabel: {
      type: String,
      default: 'Sign In',
    },
    // the module you're in, marked on its card
    current: {
      type: String,
      default: '',
    },
  },
  emits: ['pick'],
  data() {
    return {modules: ORBIT_MODULES, open: null};
  },
};
</script>

<style lang="scss" scoped>
.orbit-modules {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 24px;
  width: 100%;
  max-width: 1120px;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    max-width: 420px;
  }
}
.orbit-module {
  --tone: #2563a8;
  --tone-soft: #e8f0fa;
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #e3e8ef;
  border-top: 4px solid var(--tone);
  border-radius: 18px;
  padding: 28px 26px 26px;
  box-shadow: 0 6px 24px rgba(18, 35, 63, 0.06);
  text-align: left;
}
.orbit-module-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: var(--tone-soft);
  svg {
    width: 28px;
    height: 28px;
    fill: none;
    stroke: var(--tone);
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
}
.orbit-module-title {
  margin: 20px 0 8px;
  font-family: 'Sora', sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: #12233f;
}
.orbit-module-text {
  margin: 0 0 10px;
  font-size: 14px;
  line-height: 1.6;
  color: #5b6779;
  flex: 1;
}
.orbit-module-more {
  align-self: flex-start;
  border: 0;
  background: none;
  padding: 0;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  color: var(--tone);
  cursor: pointer;
}
.orbit-module-list {
  margin: 10px 0 0;
  padding-left: 18px;
  font-size: 13px;
  line-height: 1.7;
  color: #3d4757;
}
.orbit-module-go {
  margin-top: 22px;
  width: 100%;
  border: 0;
  border-radius: 10px;
  padding: 13px 16px;
  font: inherit;
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  background: var(--tone);
  cursor: pointer;
  &:hover,
  &:focus-visible {
    filter: brightness(1.08);
  }
}
</style>
